import { Section } from '@factorialco/deprecated-design-system'
import {
  AtsMessageTemplateNameSectionFormFragment,
  AtsUpdateMessageTemplateNameDocument,
} from 'generated/resources/graphql'

import TextField from 'modules/core/components/TextField'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface EditTemplateNameFormValues {
  name: string
}

interface Props {
  messageTemplate: AtsMessageTemplateNameSectionFormFragment
  canEdit: boolean
}

export const NameSectionForm = ({ messageTemplate, canEdit }: Props) => {
  const [updateAtsMessageTemplateName, { loading }] = useMutation(
    AtsUpdateMessageTemplateNameDocument,
    {
      extractorFn: (data) => data.atsMutations.updateAtsMessageTemplate,
    }
  )

  const { id: messageTemplateId, name } = messageTemplate

  const { values, setFieldValue, dirty, handleSubmit, resetForm, errors, isValid } =
    useForm<EditTemplateNameFormValues>({
      defaultValues: {
        name,
      },
      validate(data) {
        const formErrors: FormErrors<EditTemplateNameFormValues> = {}
        if (dirty && (!data.name || data.name === '')) {
          formErrors.name = i18n.t('common.empty')
        }
        return formErrors
      },
      async onSubmit(data, { setErrors }) {
        const result = await updateAtsMessageTemplateName({
          variables: {
            id: messageTemplateId,
            name: data.name,
          },
        })

        const handled = handleResult(result, (resultErrors) => {
          setErrors(resultErrors)
        })

        if (handled.success) {
          app.setFlash({
            type: 'correct',
            message: i18n.t('common.saved_successfully'),
          })

          resetForm({ values })
        }

        return handled
      },
    })

  return (
    <Section.Form
      illustration="document-edit"
      title={i18n.t('ats.manage_message_templates.template_form.name_title')}
      description={i18n.t('ats.manage_message_templates.template_form.name_description')}
      submitLabel={i18n.t('common.apply')}
      onSubmit={handleSubmit}
      isDirty={dirty}
      disabled={loading || !isValid}
    >
      <TextField
        label={i18n.t('ats.manage_message_templates.template_form.name_label')}
        value={values.name}
        onChange={(value) => setFieldValue('name', value)}
        readonly={!canEdit}
        error={errors.name}
      />
    </Section.Form>
  )
}
